import { createReducer, on } from '@ngrx/store';

import { AuthSessionActions } from '@ps/auth-data-access';

import { UserSettingsState } from './user-settings.model';
import {
  INITIAL_COMPANY_SETTINGS,
  INITIAL_CURRENCY_EXCHANGE_SETTINGS,
  INITIAL_USER_SETTINGS_STATE,
} from './user-settings.constants';
import {
  UserSettingsActions,
  DashboardActions,
  DashboardApiActions,
  ReportPreferenceActions,
  ReportPreferenceApiActions,
  BillingRulesApiActions,
} from './user-settings.actions';

export const userSettingsReducer = createReducer(
  INITIAL_USER_SETTINGS_STATE,

  // ---------------------------------------------------------------------------
  // Settings
  // ---------------------------------------------------------------------------

  on(UserSettingsActions.load, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(
    UserSettingsActions.loadSuccess,
    (
      state,
      { allBuyerSettings, companySettings, currencySettings, entitlements },
    ) => ({
      ...state,
      allBuyerSettings,
      companySettings: companySettings ?? INITIAL_COMPANY_SETTINGS,
      currencySettings: currencySettings ?? INITIAL_CURRENCY_EXCHANGE_SETTINGS,
      entitlements,
      loading: false,
      error: null,
    }),
  ),

  on(UserSettingsActions.loadFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(
    UserSettingsActions.rehydrate,
    (
      state,
      { allBuyerSettings, companySettings, currencySettings, entitlements },
    ) => ({
      ...state,
      allBuyerSettings,
      companySettings,
      currencySettings: currencySettings ?? INITIAL_CURRENCY_EXCHANGE_SETTINGS,
      entitlements,
    }),
  ),

  on(
    AuthSessionActions.logoutSuccess,
    (): UserSettingsState => INITIAL_USER_SETTINGS_STATE,
  ),

  on(
    UserSettingsActions.loadServiceOperatorSettingsSuccess,
    (state, { companySettings, currencySettings }) => ({
      ...state,
      companySettings: companySettings ?? INITIAL_COMPANY_SETTINGS,
      currencySettings,
    }),
  ),

  on(
    UserSettingsActions.loadServiceOperatorSettingsFailure,
    (state, { error }) => ({
      ...state,
      companySettings: INITIAL_COMPANY_SETTINGS,
      currencySettings: null,
      error,
    }),
  ),

  // ---------------------------------------------------------------------------
  // Dashboard preferences
  // ---------------------------------------------------------------------------

  on(DashboardActions.loadPreferences, (state) => ({
    ...state,
    preferencesLoading: state.preferencesCachedAt === null,
    preferencesError: null,
  })),

  on(
    DashboardApiActions.loadPreferencesSuccess,
    (state, { preferences, cachedAt }) => ({
      ...state,
      preferences,
      preferencesLoading: false,
      preferencesError: null,
      preferencesCachedAt: cachedAt,
    }),
  ),

  on(DashboardApiActions.loadPreferencesFailure, (state, { error }) => ({
    ...state,
    preferencesError: error,
    preferencesLoading: false,
  })),

  on(DashboardActions.invalidateCache, (state) => ({
    ...state,
    preferencesCachedAt: null,
  })),

  on(DashboardApiActions.updatePreferences, (state, { preferences }) => ({
    ...state,
    preferences,
    preferencesError: null,
    preferencesLoading: true,
  })),

  on(DashboardApiActions.updateSuccess, (state) => ({
    ...state,
    preferencesLoading: false,
    preferencesError: null,
  })),

  on(DashboardApiActions.updateFailure, (state, { error }) => ({
    ...state,
    preferencesLoading: false,
    preferencesError: error,
  })),

  // ---------------------------------------------------------------------------
  // Report preferences
  // ---------------------------------------------------------------------------

  on(ReportPreferenceActions.loadReportPreference, (state) => ({
    ...state,
    reportPreferencesLoading: state.reportPreferencesCachedAt === null,
    reportPreferencesError: null,
  })),

  on(
    ReportPreferenceActions.saveReportPreference,
    (state, { preferences }) => ({
      ...state,
      reportPreferences: preferences,
      reportPreferencesLoading: true,
      reportPreferencesError: null,
    }),
  ),

  on(
    ReportPreferenceApiActions.loadReportPreferenceSuccess,
    (state, { preferences, cachedAt }) => ({
      ...state,
      reportPreferences: preferences,
      reportPreferencesCachedAt: cachedAt,
      reportPreferencesLoading: false,
      reportPreferencesError: null,
    }),
  ),

  on(
    ReportPreferenceApiActions.loadReportPreferenceFailure,
    (state, { error }) => ({
      ...state,
      reportPreferences: [],
      reportPreferencesLoading: false,
      reportPreferencesError: error,
    }),
  ),

  on(ReportPreferenceApiActions.saveReportPreferenceSuccess, (state) => ({
    ...state,
    reportPreferencesLoading: false,
    reportPreferencesError: null,
  })),

  on(
    ReportPreferenceApiActions.saveReportPreferenceFailure,
    (state, { error }) => ({
      ...state,
      reportPreferencesLoading: false,
      reportPreferencesError: error,
    }),
  ),

  // ---------------------------------------------------------------------------
  // Billing Rules
  // ---------------------------------------------------------------------------
  on(
    BillingRulesApiActions.loadBillingRulesSuccess,
    (state, { record, cachedAt }) => ({
      ...state,
      billingRuleRecord: record,
      billingRulesCachedAt: cachedAt,
    }),
  ),
);
